/**
 * Permission catalog shared by the gateway (enforcement) and the web app (navigation / action gating).
 *
 * A permission is `<resource>:<action>`. Resources group Dispatch API tags by business domain;
 * actions classify what an endpoint does. Every Dispatch endpoint resolves to exactly one
 * permission through `permissionForEndpoint`, so nothing reaches the upstream API unchecked.
 */

export const RESOURCES = [
  'shipments',
  'delivery',
  'pickups',
  'returns',
  'branches',
  'linehaul',
  'warehouse',
  'finance',
  'clients',
  'fulfillment',
  'customerService',
  'reports',
  'masterData',
  'notifications',
  'admin',
  'integrations',
  'hr',
  // the recordings, two separate grants (owner, 2026-10-17: «صلاحيه لفيديو لمسات بس ميشوفش الباقي»):
  // touchVideo:read = the clip at a shipment's touch; cameras:read = the live cameras and the cameras report
  'touchVideo',
  'cameras',
  // إثبات التحويلات on its own (owner, 2026-10-30: «اقدر اديها لاي حد من غير صلاحيات المالية كلها»): upload the proof
  // of money sent to a merchant, see what is left, resend the closing sheet; nothing else of finance
  'payoutProofs',
] as const
export type Resource = (typeof RESOURCES)[number]

export const ACTIONS = ['read', 'write', 'approve', 'delete'] as const
export type Action = (typeof ACTIONS)[number]

export type Permission = `${Resource}:${Action}` | 'system:superuser'

export const ALL_PERMISSIONS: Permission[] = RESOURCES.flatMap((r) => ACTIONS.map((a) => `${r}:${a}` as Permission))

export const RESOURCE_LABELS: Record<Resource, { en: string; ar: string }> = {
  shipments: { en: 'Shipments', ar: 'الشحنات' },
  delivery: { en: 'Delivery & Runners', ar: 'التوزيع والمناديب' },
  pickups: { en: 'Pickups', ar: 'الاستلامات' },
  returns: { en: 'Returns', ar: 'المرتجعات' },
  branches: { en: 'Branches & Transfers', ar: 'الفروع والتحويلات' },
  linehaul: { en: 'Line Haul', ar: 'النقل بين المدن' },
  warehouse: { en: 'Warehouse', ar: 'المخزن' },
  finance: { en: 'Finance & COD', ar: 'المالية والتحصيل' },
  clients: { en: 'Clients', ar: 'العملاء' },
  fulfillment: { en: 'Fulfillment', ar: 'الفولفيلمنت' },
  customerService: { en: 'Customer Service', ar: 'خدمة العملاء' },
  reports: { en: 'Reports', ar: 'التقارير' },
  masterData: { en: 'Settings & Master Data', ar: 'الإعدادات والبيانات الأساسية' },
  notifications: { en: 'Notifications', ar: 'الإشعارات' },
  admin: { en: 'Administration', ar: 'الإدارة' },
  integrations: { en: 'Integrations', ar: 'التكاملات' },
  hr: { en: 'HR & Attendance', ar: 'الموارد البشرية والحضور' },
  touchVideo: { en: 'Touch video', ar: 'فيديو لمسات الشحنات' },
  cameras: { en: 'Live cameras & report', ar: 'الكاميرات المباشرة وتقريرها' },
  payoutProofs: { en: 'Transfer proofs', ar: 'إثبات التحويلات' },
}

export const ACTION_LABELS: Record<Action, { en: string; ar: string }> = {
  read: { en: 'View', ar: 'عرض' },
  write: { en: 'Create / Edit', ar: 'إضافة / تعديل' },
  approve: { en: 'Post / Settle', ar: 'ترحيل / تسوية' },
  delete: { en: 'Delete', ar: 'حذف' },
}

/** Dispatch API tag → business resource. Tags not listed here are blocked by the gateway. */
export const TAG_RESOURCE: Record<string, Resource> = {
  Shipments: 'shipments',
  MangeAWBs: 'shipments',
  MangeTransaction: 'shipments',
  MultiPieces: 'shipments',
  ShipmentNotes: 'shipments',
  ChangeStatus: 'shipments',
  LostShipments: 'shipments',
  PerformaInvoice: 'shipments',
  ExcelTemplates: 'shipments',
  Pod: 'shipments',
  VerifyAWBs: 'warehouse',
  Cells: 'warehouse',
  bncCells: 'warehouse',
  DRS: 'delivery',
  VisitResult: 'delivery',
  Runners: 'delivery',
  WebRunnerUsers: 'delivery',
  Pickup: 'pickups',
  PickupsManagement: 'pickups',
  ReturnToSender: 'returns',
  Branches: 'branches',
  TransferAWBs: 'branches',
  GroupsBranches: 'branches',
  UsersBranches: 'admin',
  HaulDashboard: 'linehaul',
  HaulRoutes: 'linehaul',
  HaulTrips: 'linehaul',
  HaulVehicles: 'linehaul',
  AgentPayment: 'finance',
  BranchPayment: 'finance',
  PayToCust: 'finance',
  Invoice: 'finance',
  CollectionReview: 'finance',
  GrossProfits: 'finance',
  PriceLists: 'finance',
  PriceListsDtls: 'finance',
  Services: 'finance',
  Receipts: 'finance',
  Currencies: 'finance',
  Clients: 'clients',
  SubAccounts: 'clients',
  WebClientUsers: 'clients',
  bncProducts: 'clients',
  CallCenter: 'customerService',
  ContactUs: 'customerService',
  ContactUsStatuses: 'customerService',
  ContactUsTypes: 'customerService',
  ReportsAndInquiry: 'reports',
  CustomReports: 'reports',
  DashBoard: 'reports',
  Zones: 'masterData',
  bncGeoCity: 'masterData',
  bncRoute: 'masterData',
  Reasons: 'masterData',
  Status: 'masterData',
  SMSTemplates: 'masterData',
  Companies: 'masterData',
  // Dispatch's system settings (/api/Settings, 2026-10-21): read with master data, changed by admins only (below)
  Settings: 'masterData',
  Notifications: 'notifications',
  Users: 'admin',
  // COREX's two-step sign-in and sessions: another user's are the admin's; his own are open to every employee
  // (OWN_SECURITY in the gateway's auth/staffSecurity.ts, EVERY_STAFF in the backend's compat/permissions.ts)
  Security: 'admin',
  CoreUsers: 'admin',
  Employees: 'admin',
  Accurate: 'integrations',
  Sindbad: 'integrations',
  Shopify: 'integrations',
  IM2Aisles: 'fulfillment',
  IM2Bins: 'fulfillment',
  IM2Clients: 'fulfillment',
  IM2Colors: 'fulfillment',
  IM2Customers: 'fulfillment',
  IM2DashBoard: 'fulfillment',
  IM2Groups: 'fulfillment',
  IM2Orders: 'fulfillment',
  IM2Products: 'fulfillment',
  IM2Racks: 'fulfillment',
  IM2SKUs: 'fulfillment',
  IM2StorageZones: 'fulfillment',
  IM2Stores: 'fulfillment',
  IM2Styles: 'fulfillment',
  IM2TransIn: 'fulfillment',
  IM2Units: 'fulfillment',
  IM2sizes: 'fulfillment',
  IM2types: 'fulfillment',
  // ShipmentArchive and Migrations are deliberately absent: database-level operations stay out of the web app.
}

const READ_VERBS = /^(get|v1get|search|inquiry|validate|valid|review|track|statushistory|preview|export|print|download|activity|custstatement|callcenterinquiry|dataentry|awbedit|canassign)|(list|inquiry)$/i
const APPROVE_VERBS = /^(post|unpost|settle|receive|receivedtl|arrive|depart|unpostcashcollection|unpostbranshcollection|unpostclientpayment|unpostdrs)/i
const DELETE_VERBS = /^delete/i

/** Classifies a Dispatch endpoint by method + the action segment of its path. */
export function actionForEndpoint(method: string, path: string): Action {
  const m = method.toUpperCase()
  const segments = path.split('/').filter((s) => s && !s.startsWith('{') && !/^v\d+$/i.test(s))
  const verb = segments[segments.length - 1] ?? ''
  if (m === 'DELETE' || DELETE_VERBS.test(verb)) return 'delete'
  if (APPROVE_VERBS.test(verb)) return 'approve'
  if (m === 'GET') return 'read'
  if (READ_VERBS.test(verb)) return 'read'
  return 'write'
}

/**
 * A runner is an employee at Dispatch: «اضافة مندوب» reads the employees, their vehicle types and adds one, so those
 * three go with the runners' permission, not admin (owner, 2026-10-21: Rowan had the page and got «مفيش صلاحية»).
 * Every other employee call (update, delete, office staff) stays admin.
 */
const RUNNER_EMPLOYEE_CALLS = /\/Employees\/(GetPage|VehicleTypesList|Add)$/i

export function permissionForEndpoint(tag: string, method: string, path: string): Permission | null {
  // a system setting changes how Dispatch treats every shipment: changing one is an admin's, reading it master data's
  if (tag === 'Settings' && actionForEndpoint(method, path) !== 'read') return 'admin:write'
  if (tag === 'Employees' && RUNNER_EMPLOYEE_CALLS.test(path)) return `delivery:${actionForEndpoint(method, path)}`
  const resource = TAG_RESOURCE[tag]
  if (!resource) return null
  return `${resource}:${actionForEndpoint(method, path)}`
}

export function hasPermission(granted: ReadonlySet<string> | readonly string[], needed: Permission | Permission[] | undefined): boolean {
  if (!needed) return true
  const set = granted instanceof Set ? granted : new Set(granted)
  if (set.has('system:superuser')) return true
  const list = Array.isArray(needed) ? needed : [needed]
  return list.every((p) => set.has(p))
}

/** Built-in role templates. The gateway seeds these; admins map VSoft roles onto them. */
export const ROLE_TEMPLATES: Record<string, { label: { en: string; ar: string }; permissions: Permission[] }> = {
  superuser: { label: { en: 'Super Administrator', ar: 'مدير النظام' }, permissions: ['system:superuser'] },
  operations: {
    label: { en: 'Operations', ar: 'العمليات' },
    permissions: [
      'shipments:read', 'shipments:write', 'shipments:approve', 'delivery:read', 'delivery:write', 'delivery:approve',
      'pickups:read', 'pickups:write', 'pickups:approve', 'returns:read', 'returns:write', 'returns:approve',
      'warehouse:read', 'warehouse:write', 'warehouse:approve', 'branches:read', 'branches:write', 'branches:approve',
      'linehaul:read', 'linehaul:write', 'linehaul:approve', 'clients:read', 'reports:read', 'masterData:read', 'notifications:read',
    ],
  },
  finance: {
    label: { en: 'Finance', ar: 'المالية' },
    permissions: [
      'finance:read', 'finance:write', 'finance:approve', 'clients:read', 'clients:write', 'shipments:read', 'delivery:read',
      'branches:read', 'returns:read', 'reports:read', 'masterData:read', 'notifications:read',
    ],
  },
  customerService: {
    label: { en: 'Customer Service', ar: 'خدمة العملاء' },
    permissions: [
      'customerService:read', 'customerService:write', 'shipments:read', 'clients:read', 'delivery:read', 'pickups:read',
      'returns:read', 'reports:read', 'masterData:read', 'notifications:read',
    ],
  },
  viewer: {
    label: { en: 'Read only', ar: 'عرض فقط' },
    permissions: RESOURCES.filter((r) => r !== 'admin').map((r) => `${r}:read` as Permission),
  },
}
